class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      
      // Store the line item info before attempting removal
      const lineIndex = this.dataset.index;
      
      // First try normal removal
      cartItems.updateQuantity(lineIndex, 0);
      
      // Set up error monitoring - if the cart update failed with JSON error,
      // we'll try a direct API approach as fallback
      setTimeout(() => {
        const errorElement = document.querySelector('.cart-errors');
        const drawerErrorElement = document.querySelector('.drawer__cart-errors');
        const hasError = (errorElement && errorElement.textContent) || 
                         (drawerErrorElement && drawerErrorElement.textContent);
        
        if (hasError || document.querySelector('.cart-json-error')) {
          console.log('❗ Detected error after cart removal, trying fallback method');
          this.removeItemDirectly(lineIndex);
        }
      }, 800); // Check after a short delay
    });
  }
  
  // Alternative method to remove items that bypasses the JSON parser
  removeItemDirectly(lineIndex) {
    console.log('🛠️ Using direct cart removal API for line:', lineIndex);
    
    // Direct AJAX call to the cart/change endpoint with minimal data
    const formData = new FormData();
    formData.append('line', lineIndex);
    formData.append('quantity', 0);
    
    fetch('/cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .catch(error => {
      console.error('Error in direct removal:', error);
      // Force reload as last resort
      window.location.reload();
    })
    .then(cart => {
      if (cart && typeof cart === 'object') {
        console.log('✅ Direct removal successful, refreshing cart');
        // Refresh the cart UI
        window.location.reload();
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          updateCart();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        let parsedState;
        
        try {
          parsedState = JSON.parse(state);
        } catch (parseError) {
          console.error('❌ Error parsing cart response:', parseError);
          console.log('Response that failed to parse:', state.substring(0, 500) + '...');
          
          // Try to fix common JSON syntax errors
          try {
            // Special handling for the specific error we're getting at position ~8160
            if (parseError.message.includes("position 816")) {
              console.log('Detected the specific 8160 position error, attempting targeted fix...');
              
              // Get a window of characters around the error position
              const startPos = Math.max(0, 8160 - 20);
              const endPos = Math.min(state.length, 8160 + 20);
              const problematicSection = state.substring(startPos, endPos);
              console.log('Problematic section:', problematicSection);
              
              // Create a fixed version of the JSON string
              let fixedState = state;
              
              // If we can identify a specific pattern at this position,
              // we can implement a targeted fix
              
              // Common issues around position 8160 could be:
              // 1. A missing comma between array elements
              // 2. A trailing comma at the end of an array
              // 3. Unescaped quotes or control characters
              
              // Apply specific fixes for position 8160
              // First check if there's a pattern like 'something"something' without a comma
              fixedState = state.substring(0, 8160).replace(/(\w+")\s*("[a-zA-Z])/g, '$1,$2');
              fixedState += state.substring(8160);
              
              // Also check for trailing commas in arrays or objects
              fixedState = fixedState.replace(/,\s*([}\]])/g, '$1');
              
              // Check for unbalanced brackets and add missing ones
              const openBrackets = (fixedState.match(/\[/g) || []).length;
              const closeBrackets = (fixedState.match(/\]/g) || []).length;
              if (openBrackets > closeBrackets) {
                fixedState += ']'.repeat(openBrackets - closeBrackets);
              }
              
              const openBraces = (fixedState.match(/\{/g) || []).length;
              const closeBraces = (fixedState.match(/\}/g) || []).length;
              if (openBraces > closeBraces) {
                fixedState += '}'.repeat(openBraces - closeBraces);
              }
              
              // As a last resort, we can try to directly fix the JSON at position 8160
              // by inserting a comma or removing a problematic character
              if (fixedState.length >= 8162) {
                // Try different approaches: insert comma, remove character, etc.
                const attempts = [
                  // Add a comma
                  fixedState.slice(0, 8160) + ',' + fixedState.slice(8160),
                  // Replace character with comma
                  fixedState.slice(0, 8160) + ',' + fixedState.slice(8161),
                  // Remove character
                  fixedState.slice(0, 8160) + fixedState.slice(8161),
                  // Replace with proper array ending
                  fixedState.slice(0, 8160) + ']}' + fixedState.slice(8162)
                ];
                
                // Try each fix attempt in order
                for (const attempt of attempts) {
                  try {
                    console.log('Trying specific repair attempt...');
                    parsedState = JSON.parse(attempt);
                    console.log('✅ Fix successful!');
                    break; // If successful, exit the loop
                  } catch (e) {
                    console.log('Attempt failed:', e.message);
                    // Continue to next attempt
                  }
                }
                
                // If we have a parsed state, we can skip the other fixes
                if (parsedState) {
                  console.log('Successfully fixed JSON with targeted approach');
                  // Continue with processing the parsed state
                } else {
                  // Last resort: If we can't fix it, create a minimal valid state
                  console.log('All repair attempts failed, creating minimal state object');
                  parsedState = {
                    item_count: 0,
                    items: [],
                    sections: {}
                  };
                  this.fetchCurrentCartState();
                }
              } else {
                throw new Error("JSON string too short to apply position-specific fix");
              }
            } else if (parseError.message.includes("Expected ',' or ']'") || 
                parseError.message.includes("Expected ',' or '}'")) {
              // Original fix logic for other cases
              let sanitizedState = state;
              
              // Remove any trailing commas in arrays or objects
              sanitizedState = sanitizedState.replace(/,\s*([}\]])/g, '$1');
              
              // Add missing closing brackets/braces if needed
              const openBraces = (sanitizedState.match(/\{/g) || []).length;
              const closeBraces = (sanitizedState.match(/\}/g) || []).length;
              if (openBraces > closeBraces) {
                sanitizedState += '}'.repeat(openBraces - closeBraces);
              }
              
              const openBrackets = (sanitizedState.match(/\[/g) || []).length;
              const closeBrackets = (sanitizedState.match(/\]/g) || []).length;
              if (openBrackets > closeBrackets) {
                sanitizedState += ']'.repeat(openBrackets - closeBrackets);
              }
              
              // Try parsing again with the fixed JSON
              console.log('Attempting to parse sanitized JSON');
              parsedState = JSON.parse(sanitizedState);
              console.log('✅ Successfully parsed sanitized JSON');
            } else {
              // If we can't fix it, throw the original error
              throw parseError;
            }
          } catch (secondError) {
            console.error('❌ Failed to fix JSON parsing issue:', secondError);
            
            // If all our fixes failed, create a basic state object
            console.log('Creating minimal cart state object for recovery');
            parsedState = {
              item_count: 0,
              items: [],
              sections: {}
            };
            
            // Also fetch current cart state as backup
            this.fetchCurrentCartState();
          }
        }
        
        // Continue only if we have a valid parsedState object
        if (!parsedState || typeof parsedState !== 'object') {
          console.error('Invalid parsed state, using fallback empty cart');
          parsedState = {
            item_count: 0,
            items: [],
            sections: {}
          };
        }
        
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Handle empty cart case first
        if (parsedState.item_count === 0) {
          // Get cart drawer and refresh it
          if (cartDrawerWrapper) {
            cartDrawerWrapper.renderContents(parsedState);
            // Don't try to focus on elements that don't exist
            return;
          }
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        // Only try to access items array if there are items
        let updatedValue;
        let message = '';
        
        if (parsedState.items && parsedState.items.length > 0 && line - 1 < parsedState.items.length) {
          updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
        }
        
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        console.error('Error updating cart:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        
        // Mark that we had a JSON error for the fallback mechanism
        if (error.toString().includes('JSON')) {
          this.markJsonError();
        }
        
        // In case of an error, try to fetch current cart state
        this.fetchCurrentCartState();
      })
      .finally(() => {
        this.disableLoading(line);
        try {
          updateCart();
        } catch (e) {
          console.error('Error in updateCart:', e);
        }
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  // Add a new method to fetch current cart state directly
  fetchCurrentCartState() {
    console.log('Fetching current cart state as fallback');
    
    // Fetch the current cart state
    fetch(`${routes.cart_url}.js`)
      .then(response => response.json())
      .then(cart => {
        console.log('Current cart state:', cart);
        
        // Update UI based on cart state
        this.classList.toggle('is-empty', cart.item_count === 0);
        
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', cart.item_count === 0);
          
          // If cart is empty and we're in the drawer, refresh the drawer
          if (cart.item_count === 0 && this.tagName === 'CART-DRAWER-ITEMS') {
            // Create a minimal parsedState object with just the info we need
            const minimalState = {
              item_count: 0,
              items: []
            };
            
            cartDrawerWrapper.renderContents(minimalState);
          } else {
            // Otherwise force refresh the drawer content
            cartDrawerWrapper.fetchCartDrawerContents();
          }
        }
        
        // Update cart count on icon
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countBubble = cartIconBubble.querySelector('.cart-count-bubble');
          if (countBubble && cart.item_count > 0) {
            const countText = countBubble.querySelector('span[aria-hidden="true"]');
            if (countText) countText.textContent = cart.item_count;
          } else if (countBubble) {
            countBubble.style.display = 'none';
          }
        }
      })
      .catch(error => {
        console.error('Error fetching current cart state:', error);
      });
  }

  // Helper to mark JSON errors for the fallback mechanism
  markJsonError() {
    // Create or update hidden marker element
    let errorMarker = document.querySelector('.cart-json-error');
    if (!errorMarker) {
      errorMarker = document.createElement('div');
      errorMarker.className = 'cart-json-error';
      errorMarker.style.display = 'none';
      document.body.appendChild(errorMarker);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorMarker && errorMarker.parentNode) {
          errorMarker.parentNode.removeChild(errorMarker);
        }
      }, 5000);
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
